import { Route, Routes, useLocation } from 'react-router-dom';
import UserManagement from './containers/user-container/index.js';
import BotManagement from './containers/bot-container/index.js';
import AnalyticsAndInsights from './containers/analytics-container/index.js';
import AnnouncementsAndNews from './containers/announcement-container/index.js';
import VerificationRequest from './containers/verification-container/index.js';
import SupportTickets from './containers/support-container/index.js';
import PopupNotifications from './containers/popup-container/index.js';
import FinancialsPlans from './containers/financial-container/index.js';
import Subscription from './containers/subscription-container/index.js';
import LocationInsights from './containers/location-container/index.js';
import Emails from './containers/email-container/index.js';
import Reports from './containers/report-container/index.js';
import ProtectedRoute from './hoc/ProtectedRoute';
import AdminLogin from './containers/auth-container/AdminLogin.js';
import ChatApp from './containers/chat-container/index.js';
import './assets/css/App.css';
import QueryTicketManagement from './containers/query-ticket-management/QueryTicketManagement.js';
import TicketConversation from './containers/query-ticket-management/TicketConversation.js';
import DashboardOverview from './containers/dashboard-overview/index.js';
import SignupReport from './containers/signup-report-container/index.js';
import ExpenseOverview from './containers/expense-overview/ExpenseOverview.js';
import MUIBasedPortfolio from './components/ChatMessenger/bot-profile-chat/MUIBasedPortfolio.js';
import UserProfileMainPage from './components/user-profile-main/UserProfileMainPage.js';
import AnalyticsComparison from './components/analytics-comparison/AnalyticsComparison.js';
import GoogleAnalyticsAdsOverview from './containers/google-analytics-container/GoogleAnalyticsAdsOverview.js';
import useSocket from './hooks/useSocket.js';
import { useEffect } from 'react';
import DeletedAccountManagement from './containers/deleted-accounts-container/index.js';
import UserListCampaign from './containers/user-list-campaign/index.js';
import AllCampaignPage from './containers/campaign-container/index.js';
import PaymentAnalytics from './components/payment-analytics/PaymentAnalytics.js';
import EmailAnalytics from './containers/email-analytics-container/index.js';
import ChannelAttribution from './containers/channel-attribution-container/index.js';
import ChannelAttributionMembers from './containers/channel-attribution-container/Members.js';
import GhostFunnel from './containers/channel-attribution-container/GhostFunnel.js';
import DailyActives from './containers/daily-actives-container/index.js';
import OnlineNow from './containers/online-now-container/index.js';
import Engagement from './containers/engagement-container/index.js';
import MemberEngagement from './containers/member-engagement-container/index.js';
import MessagingActivity from './containers/messaging-activity-container/index.js';
import BotCoverage from './containers/bot-coverage-container/index.js';
import AiShortlist from './containers/ai-shortlist-container/index.js';
import MemberHistory from './containers/messaging-activity-container/MemberHistory.js';
import SubscriptionReport from './containers/subscription-report-container/index.js';
import ProfileActivations from './containers/profile-activations-container/index.js';
import ActivationCohortDay from './containers/activation-cohort-day-container/index.js';
// import TicketConversation from './containers/query-ticket-management/TicketConversation.js';

const BASE_TITLE = 'Admin Dashboard - Favor';

const ROUTE_TITLES = {
  '/': 'Dashboard Overview',
  '/signup-report': 'Signup Report',
  '/location-insights': 'Location Insights',
  '/user-management': 'User Management',
  '/bot-management': 'Bot Management',
  '/deleted-accounts': 'Deleted Accounts',
  '/analytics': 'Analytics & Insights',
  '/announcements': 'Announcements & News',
  '/verification': 'Verification Requests',
  '/support-tickets': 'Support Tickets',
  '/popups': 'Pop-up Notifications',
  '/chat': 'Chat',
  '/financials': 'Financials & Plans',
  '/subscription': 'Subscription',
  '/emails': 'Emails',
  '/reports': 'Reports',
  '/QueryTicketManagement': 'Query Ticket Management',
  '/payment-analytics': 'Payment Analytics',
  '/email-analytics': 'Email Analytics',
  '/engagement': 'Engagement',
  '/member-engagement': 'Member Engagement',
  '/messaging-activity': 'Messaging Activity',
  '/bot-coverage': 'Bot Coverage',
  '/ai-shortlist': 'AI Shortlist',
  '/TicketConversation': 'Ticket Conversation',
  '/expense': 'Expense Overview',
  '/google-analytics': 'Google Analytics & Ads',
  '/MUIBasedPortfolio': 'Portfolio',
  '/UserProfileMainPage': 'User Profile',
  '/AnalyticsComparison': 'Analytics Comparison',
  '/login': 'Login',
  '/utm-campaigns': 'UTM Campaigns',
  '/channel-attribution': 'Channel Attribution',
  '/attribution-members': 'Attribution Members',
  '/subscription-report': 'Subscription Report',
  '/profile-activations': 'Profile Activations',
  '/activation-cohort-day': 'Cohort Day Detail',
  '/onboarding-dropoff': 'Onboarding Drop-off',
  '/daily-actives': 'Daily Actives',
  '/online-now': 'Online Right Now',
};

const getRouteTitle = (pathname) => {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith('/messaging-activity/')) return 'Member History';
  if (pathname.startsWith('/user-list-campaign/')) return 'Campaign Users';
  return null;
};

function App() {
  const { socket } = useSocket();
  const location = useLocation()

  useEffect(() => {
    const pageTitle = getRouteTitle(location?.pathname);
    document.title = pageTitle ? `${pageTitle} | ${BASE_TITLE}` : BASE_TITLE;
  }, [location?.pathname])

  useEffect(() => {
    if (location?.pathname != "chat" && socket) {
      const previousRoomId = localStorage.getItem("chatRoomId");
      const previousSenderId = localStorage.getItem("chatSenderId");

      if (previousRoomId) {
        socket.emit("leaveRoom", { roomId: previousRoomId, userId: previousSenderId });
      }
      localStorage.removeItem("chatRoomId");
      localStorage.removeItem("chatSenderId");
    }
  }, [location, socket])


  return (
    <Routes>
      <Route
        path="/"
        element={<ProtectedRoute ChildComponent={DashboardOverview} />}
      />
      <Route
        path="/signup-report"
        element={<ProtectedRoute ChildComponent={SignupReport} />}
      />
      <Route
        path="/location-insights"
        element={<ProtectedRoute ChildComponent={LocationInsights} />}
      />
      <Route
        path="/user-management"
        element={<ProtectedRoute ChildComponent={UserManagement} />}
      />
      <Route
        path="/bot-management"
        element={<ProtectedRoute ChildComponent={BotManagement} />}
      />
      <Route
        path="/deleted-accounts"
        element={<ProtectedRoute ChildComponent={DeletedAccountManagement} />}
      />
      <Route
        path="/analytics"
        element={<ProtectedRoute ChildComponent={AnalyticsAndInsights} />}
      />
      <Route
        path="/announcements"
        element={<ProtectedRoute ChildComponent={AnnouncementsAndNews} />}
      />
      <Route
        path="/verification"
        element={<ProtectedRoute ChildComponent={VerificationRequest} />}
      />
      <Route
        path="/support-tickets"
        element={<ProtectedRoute ChildComponent={SupportTickets} />}
      />
      <Route
        path="/popups"
        element={<ProtectedRoute ChildComponent={PopupNotifications} />}
      />
      <Route
        path="/chat"
        element={<ProtectedRoute ChildComponent={ChatApp} />}
      />
      <Route
        path="/financials"
        element={<ProtectedRoute ChildComponent={FinancialsPlans} />}
      />
      <Route
        path="/subscription"
        element={<ProtectedRoute ChildComponent={Subscription} />}
      />

      <Route
        path="/emails"
        element={<ProtectedRoute ChildComponent={Emails} />}
      />

      <Route
        path="/reports"
        element={<ProtectedRoute ChildComponent={Reports} />}
      />
      <Route
        path="/QueryTicketManagement"
        element={<ProtectedRoute ChildComponent={QueryTicketManagement} />}
      />
      <Route
        path="/payment-analytics"
        element={<ProtectedRoute ChildComponent={PaymentAnalytics} />}
      />
      <Route
        path="/email-analytics"
        element={<ProtectedRoute ChildComponent={EmailAnalytics} />}
      />
      <Route
        path="/engagement"
        element={<ProtectedRoute ChildComponent={Engagement} />}
      />
      <Route
        path="/member-engagement"
        element={<ProtectedRoute ChildComponent={MemberEngagement} />}
      />
      <Route
        path="/messaging-activity"
        element={<ProtectedRoute ChildComponent={MessagingActivity} />}
      />
      <Route
        path="/messaging-activity/:id"
        element={<ProtectedRoute ChildComponent={MemberHistory} />}
      />
      <Route
        path="/bot-coverage"
        element={<ProtectedRoute ChildComponent={BotCoverage} />}
      />
      <Route
        path="/ai-shortlist"
        element={<ProtectedRoute ChildComponent={AiShortlist} />}
      />
      <Route
        path="/subscription-report"
        element={<ProtectedRoute ChildComponent={SubscriptionReport} />}
      />
      <Route
        path="/profile-activations"
        element={<ProtectedRoute ChildComponent={ProfileActivations} />}
      />
      <Route
        path="/activation-cohort-day"
        element={<ProtectedRoute ChildComponent={ActivationCohortDay} />}
      />
      <Route
        path="/TicketConversation"
        element={<ProtectedRoute ChildComponent={TicketConversation} />}
      />
      <Route
        path="/expense"
        element={<ProtectedRoute ChildComponent={ExpenseOverview} />}
      />
      <Route
        path="/google-analytics"
        element={<ProtectedRoute ChildComponent={GoogleAnalyticsAdsOverview} />}
      />
      <Route
        path="/MUIBasedPortfolio"
        element={<ProtectedRoute ChildComponent={MUIBasedPortfolio} />}
      />
      <Route
        path="/UserProfileMainPage"
        element={<ProtectedRoute ChildComponent={UserProfileMainPage} />}
      />
      <Route
        path="/AnalyticsComparison"
        element={<ProtectedRoute ChildComponent={AnalyticsComparison} />}
      />
      <Route path="/login" element={<AdminLogin />} />


      <Route
        path="/utm-campaigns"
        element={<ProtectedRoute ChildComponent={AllCampaignPage} />}
      />
      <Route
        path="/user-list-campaign/:type/:utmId"
        element={<ProtectedRoute ChildComponent={UserListCampaign} />}
      />
      <Route
        path="/channel-attribution"
        element={<ProtectedRoute ChildComponent={ChannelAttribution} />}
      />
      <Route
        path="/attribution-members"
        element={<ProtectedRoute ChildComponent={ChannelAttributionMembers} />}
      />
      <Route
        path="/onboarding-dropoff"
        element={<ProtectedRoute ChildComponent={GhostFunnel} />}
      />
      <Route
        path="/daily-actives"
        element={<ProtectedRoute ChildComponent={DailyActives} />}
      />
      <Route
        path="/online-now"
        element={<ProtectedRoute ChildComponent={OnlineNow} />}
      />

    </Routes>
  );
}

export default App;
